import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const faqQuestions = [
    {
      '@type': 'Question',
      name: '17c 平台的主要服务内容是什么？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '17c 平台是一个综合性的数字化服务平台，提供智能数据管理、协同办公、智能分析引擎、安全保障体系和开放 API 接口等核心功能模块。',
      },
    },
    {
      '@type': 'Question',
      name: '如何联系 17c 平台客服？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过以下方式联系我们：访问关于我们页面获取联系信息、发送邮件至 support@17c.com、拨打客服电话 400-888-8888（周一至周五 9:00-18:00）。',
      },
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - http //www.17c/ 官方入口与使用指南',
          description: '了解 17c 平台的品牌介绍、发展历程、行业声誉与联系方式，http //www.17c/ 官方入口。',
          url: 'https://www.17c.com/about',
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqQuestions,
          }),
        }}
      />

      <Navbar />
      <main className="min-h-screen bg-background-50">
        <div className="pt-24 pb-10 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 pb-16">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于 17c 平台
              </h1>
              <p className="text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                {aboutData.brandIntro}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mb-16">
              {aboutData.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-flag-line text-lg"></i>
                </div>
                <h2 className="font-heading font-semibold text-foreground-900 mb-3 text-lg">我们的使命</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.mission}</p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-eye-line text-lg"></i>
                </div>
                <h2 className="font-heading font-semibold text-foreground-900 mb-3 text-lg">我们的愿景</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.vision}</p>
              </div>
            </div>

            <div id="reputation" className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
                品牌声誉
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {aboutData.reputations.map((rep) => (
                  <div
                    key={rep.title}
                    className="p-6 rounded-xl bg-background-100 border border-background-200/70"
                  >
                    <h3 className="font-heading font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                      <i className="ri-verified-badge-line text-accent-600"></i>
                      {rep.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{rep.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60"></div>
                <div className="space-y-10">
                  {aboutData.milestones.map((milestone, index) => {
                    const isEven = index % 2 === 0;
                    return (
                      <div key={milestone.year} className="relative pl-10 md:pl-0">
                        <div className={`md:w-1/2 ${isEven ? 'md:pr-10 md:ml-0 md:mr-auto' : 'md:pl-10 md:ml-auto md:mr-0'}`}>
                          <div className={`p-5 rounded-xl bg-background-100 border border-background-200/70 ${isEven ? 'md:text-right' : 'md:text-left'}`}>
                            <span className="inline-block px-3 py-1 text-xs font-bold rounded-full bg-primary-100 text-primary-700 mb-2">
                              {milestone.year}
                            </span>
                            <h3 className="font-heading font-semibold text-foreground-900 mb-1 text-sm">
                              {milestone.title}
                            </h3>
                            <p className="text-xs text-foreground-600 leading-relaxed">{milestone.description}</p>
                          </div>
                        </div>
                        <div className="absolute left-0 md:left-1/2 top-3 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 -translate-x-1/2 z-10">
                          <i className="ri-check-line text-sm"></i>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div id="contact" className="rounded-2xl bg-background-100 border border-background-200/70 p-8 md:p-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-5">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <h3 className="font-medium text-foreground-900 text-sm mb-1">公司地址</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{aboutData.contact.address}</p>
                </div>
                <div className="text-center p-5">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-3">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h3 className="font-medium text-foreground-900 text-sm mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-600">{aboutData.contact.email}</p>
                </div>
                <div className="text-center p-5">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <h3 className="font-medium text-foreground-900 text-sm mb-1">客服电话</h3>
                  <p className="text-xs text-foreground-600">{aboutData.contact.phone}</p>
                </div>
                <div className="text-center p-5">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-3">
                    <i className="ri-time-line text-xl"></i>
                  </div>
                  <h3 className="font-medium text-foreground-900 text-sm mb-1">工作时间</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{aboutData.contact.workingHours}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}